import {
  CreateRecurringIssueParams,
  RecurringIssue,
  RecurringIssueFilter,
} from "./types";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { showNotification } from "@store/ui/uiThunk";
import l from "@locale";
import { format } from "date-fns";
import { RootState } from "@store";
import api from "@services/api";
import { createAttachments } from "@services/helpers";
import { Attachment } from "@/types/common";

const mapAttachment = (
  issue: RecurringIssue,
  fallbackUrl?: string,
): Attachment | undefined => {
  const url = issue.attachmentUrl ?? fallbackUrl;
  if (!url) return issue.attachment;
  return { id: issue.hktdAttachmentId, url };
};

const uploadAttachmentIfBlob = async (attachment?: Attachment) => {
  if (!attachment?.url?.startsWith("blob:")) {
    return { attachment, id: attachment?.id };
  }

  const [uploaded] = await createAttachments([{ url: attachment.url }]);
  return { attachment: uploaded, id: uploaded?.id };
};

const fetchRecurringIssues = createAsyncThunk<
  RecurringIssue[],
  RecurringIssueFilter | undefined,
  { state: RootState }
>("recurringIssues/fetch", async (filter: RecurringIssueFilter | undefined) => {
  const response = await api.RecurringIssues.getAll(filter ?? {});
  return response.data.map((issue) => ({
    ...issue,
    attachment: mapAttachment(issue),
  }));
});

const deleteRecurringIssue = createAsyncThunk<
  RecurringIssue["id"],
  { id: RecurringIssue["id"]; label: RecurringIssue["label"] },
  { state: RootState }
>("recurringIssues/delete", async ({ id, label }, { dispatch }) => {
  const value = l.t("common.tasks");
  try {
    await api.RecurringIssues.delete(id);
    dispatch(
      showNotification({
        type: "success",
        message: l.t("tasks.delete.result", { label }),
      }),
    );
    return id;
  } catch (error) {
    dispatch(
      showNotification({
        type: "error",
        message: l.t("actions.delete.failed", { value }),
      }),
    );
    throw error;
  }
});

const createRecurringIssue = createAsyncThunk<
  RecurringIssue,
  {
    issue: Omit<RecurringIssue, "id">;
    onSuccess: () => void;
  },
  { state: RootState }
>(
  "recurringIssues/create",
  async (
    {
      issue,
      onSuccess,
    }: { issue: Omit<RecurringIssue, "id">; onSuccess: () => void },
    { dispatch },
  ) => {
    const { attachment, ...issuePayload } = issue;
    const { attachment: resolvedAttachment, id: finalAttachmentId } =
      await uploadAttachmentIfBlob(attachment);

    const newIssue: CreateRecurringIssueParams = {
      ...issuePayload,
      hktdSpaceIds: issue.spaceIds,
      startDate: format(new Date(issue.startDate), "yyyy-MM-dd"),
      // if endDate is set, format it to "yyyy-MM-dd", otherwise set it to null
      endDate: issue.endDate
        ? format(new Date(issue.endDate), "yyyy-MM-dd")
        : null,
      hktdAttachmentId: finalAttachmentId,
    };

    try {
      const response = await api.RecurringIssues.create(newIssue);
      dispatch(
        showNotification({
          type: "success",
          message: l.t("tasks.create.result", { label: response.label }),
          title: l.t("tasks.create.success"),
        }),
      );
      onSuccess();
      return {
        ...response,
        attachment: mapAttachment(response, resolvedAttachment?.url),
      };
    } catch (error) {
      dispatch(
        showNotification({
          type: "error",
          message: l.t("tasks.create.failed"),
        }),
      );
      throw error;
    }
  },
);

const updateRecurringIssue = createAsyncThunk<
  RecurringIssue,
  {
    id: RecurringIssue["id"];
    issue: Omit<RecurringIssue, "id">;
    onSuccess: () => void;
  },
  { state: RootState }
>(
  "recurringIssues/update",
  async (
    {
      id,
      issue,
      onSuccess,
    }: {
      id: RecurringIssue["id"];
      issue: Omit<RecurringIssue, "id">;
      onSuccess: () => void;
    },
    { dispatch },
  ) => {
    const { attachment, ...issuePayload } = issue;
    const { attachment: resolvedAttachment, id: finalAttachmentId } =
      await uploadAttachmentIfBlob(attachment);

    const newIssue: CreateRecurringIssueParams = {
      ...issuePayload,
      hktdSpaceIds: issue.spaceIds,
      startDate: format(new Date(issue.startDate), "yyyy-MM-dd"),
      endDate: issue.endDate
        ? format(new Date(issue.endDate), "yyyy-MM-dd")
        : null,
      hktdAttachmentId: finalAttachmentId,
    };
    try {
      const response = await api.RecurringIssues.update(id, newIssue);
      dispatch(
        showNotification({
          type: "success",
          message: l.t("tasks.update.result", { label: response.label }),
          title: l.t("tasks.update.success"),
        }),
      );
      onSuccess();
      return {
        ...response,
        attachment: mapAttachment(response, resolvedAttachment?.url),
      };
    } catch (error) {
      dispatch(
        showNotification({
          type: "error",
          message: l.t("tasks.update.failed"),
        }),
      );
      throw error;
    }
  },
);

export {
  fetchRecurringIssues,
  deleteRecurringIssue,
  createRecurringIssue,
  updateRecurringIssue,
};
